import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Faq from "@/pages/home/components/Faq";
import { MAIN_SITE_URL } from "@/constants";
import { articleImages, articleOrder } from "@/mocks/articles";

interface Section {
  heading: string;
  paragraphs: string[];
}

interface FaqItem {
  q: string;
  a: string;
}

interface ArticleContent {
  category: string;
  title: string;
  subtitle: string;
  updated: string;
  readTime: string;
  quickAnswer: string;
  sections: Section[];
  faqs: FaqItem[];
}

export default function ArticlePage() {
  const { slug } = useParams();
  const { t } = useTranslation();

  const articles = t("articles", { returnObjects: true }) as unknown as Record<
    string,
    ArticleContent
  >;
  const article = slug ? articles[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const image = articleImages[slug as string];

  const related = articleOrder
    .filter((s) => s !== slug)
    .slice(0, 3)
    .map((s) => ({ slug: s, title: articles[s]?.title }))
    .filter((r) => r.title);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <article>
          <header className="bg-background-100">
            <div className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
                <Link to="/" className="transition-colors hover:text-primary-700">
                  {t("brand")}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">{article.category}</span>
              </nav>

              <h1 className="mt-5 text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
                {article.subtitle}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 font-medium text-primary-700">
                  <i className="ri-calendar-line"></i>
                  {article.updated}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>
          </header>

          <div className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
            {image && (
              <div className="mb-10 overflow-hidden rounded-2xl border border-background-200 bg-white">
                <img
                  src={image}
                  alt={article.title}
                  title={`${t("brand")} ${article.title}`}
                  className="h-56 w-full object-cover object-top md:h-72"
                />
              </div>
            )}

            <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 text-sm font-bold text-primary-700">
                <i className="ri-flashlight-line"></i>
                {t("article.quickAnswerTitle")}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-base">
                {article.quickAnswer}
              </p>
            </div>

            <div className="mt-10 space-y-10">
              {article.sections.map((section, index) => (
                <section key={index}>
                  <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph, pIndex) => (
                      <p
                        key={pIndex}
                        className="text-sm leading-relaxed text-foreground-700 md:text-base"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-12 rounded-2xl bg-background-100 p-5 md:p-6">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
                {t("article.faqTitle")}
              </h2>
              <div className="mt-5">
                <Faq items={article.faqs} />
              </div>
            </div>

            <div className="mt-12 rounded-2xl bg-primary-500 p-6 text-center md:p-8">
              <h2 className="text-xl font-bold text-white md:text-2xl">
                {t("article.ctaTitle")}
              </h2>
              <p className="mt-3 text-sm text-white/90 md:text-base">{t("article.ctaBody")}</p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
                >
                  {t("article.ctaButton")}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("article.ctaSecondary")}
                </Link>
              </div>
            </div>

            <div className="mt-10 border-t border-background-200 pt-6">
              <h3 className="text-sm font-semibold text-foreground-950">
                {t("article.relatedTitle")}
              </h3>
              <p className="mt-1 text-sm text-foreground-600">{t("article.relatedText")}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-home-4-line"></i>
                  {t("brand")}
                </Link>
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/guide/${r.slug}`}
                    className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-file-text-line"></i>
                    {r.title}
                  </Link>
                ))}
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-external-link-line"></i>
                  {t("article.ctaButton")}
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}